import React, { useMemo, useState, useRef, useEffect } from "react"
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import gql from 'graphql-tag'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, Progress, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import Select from 'react-select'
import moment from "moment"
import { debounce } from 'lodash'

// import VehiclePartyModal from "./AddVehicleModal";
// import AddPeopleModal from "./AddPeopleModal";
import RenderArgyleGigs from "../Components/RenderArgyleGigs"
import "./Modals.css"
import toTitleCase from "../../Functions/toTitleCase"
import { IncidentSchema } from "./IncidentModalsSchema"
import { PreferredDateTimeContext } from "../../Context/PreferredDateTimeContext"
import MapPicker from "../../Material/MapPicker"
import DatetimePickerWithTimeZone from "../../Material/DatetimePickerWithTimeZone"
import { GetConfigurationSettings } from "../../Functions/querys"
import { getStatesByCountry } from "../../utils"

const REPORTED_BY_ENUM_QUERY = gql`
  query GetReportedByChoices {
    reportedByChoices: __type(name: "IncidentsIncidentReportedByChoices") {
      enumValues { name description }
    }
  }
`

const ALL_INCIDENT_DOCUMENT_TYPES = gql`
  query AllIncidentDocumentTypes {
    allIncidentDocumentTypes(category: "INCIDENT", isActive: true) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`



const CREATE_NEW_INCIDENT_MUTATION = gql`
  mutation CreateNewIncident($input: CreateNewIncidentMutationInput!) {
    createNewIncident(input: $input) {
      ok
      errors { field messages }
    }
  }
`

const ALL_ENUMS = gql`
  query AllEnums {
    ridesharePlatformChoices: __type(name: "IncidentsIncidentRidesharePlatformChoices") {
      enumValues { name description }
    }
    ridesharePeriodChoices: __type(name: "IncidentsIncidentRidesharePeriodChoices") {
      enumValues { name description }
    }
    ridesharePeriodSourceChoices: __type(name: "IncidentsIncidentRidesharePeriodSourceChoices") {
      enumValues { name description }
    }
    # responsiblePartyChoices: __type(name: "IncidentsIncidentResponsiblePartyChoices") {
    #   enumValues { name description }
    # }
    # sourceChoices: __type(name: "IncidentsIncidentSourceChoices") {
    #   enumValues { name description }
    # }
    incidentTypeChoices: __type(name: "IncidentsIncidentIncidentTypeChoices") {
      enumValues { name description }
    }
    carTotaledChoices: __type(name: "IncidentsIncidentCarTotaledChoices") {
      enumValues { name description }
    }
  }
`

const ALL_CARS_WITH_AGREEMENTS_QUERY = gql`
  query AllCars($searchTerm: String) {
    allCars(searchTerm: $searchTerm) {
      edges {
        node {
          id pk year color group vin
          agreementSet(orderBy:["-id"]) {
            edges {
              node {
                id pk startDate endDate agreementType
                driver { id name }
              }
            }
          }
          carModel {
            id make name series
            groupType { id name }
          }
        }
      }
    }
  }
`

const ALL_RENTALS_QUERY = gql`query AllRentals($carId: ID!, $activeOn:Date!) {
    allRentals(car_Id:$carId, activeOn:$activeOn) {
        edges {
            node {
                id pk startDate endDate agreementType
                driver { id name }
            }
        }
    }
}`


export default function AddNewIncidentModal({ isOpen, toggle, refetchQuery, carId, agreementId, carConditionId, maxIncidentDate, branchId }) {
    const [step, setStep] = useState(1)
    // const [parties, setParties] = useState([]);
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    // const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
    // const [peopleModalOpen, setPeopleModalOpen] = useState(false);
    // const [peoplePartyType, setPeoplePartyType] = useState(null);
    const [documents, setDocuments] = useState([])
    const [selectedDocType, setSelectedDocType] = useState(null)
    const [docError, setDocError] = useState(null)
    const [showAddDocument, setShowAddDocument] = useState(true)
    const [selectedCar, setSelectedCar] = useState(carId)
    const [selectedCarObj, setSelectedCarObj] = useState(null)
    const [agreements, setAgreements] = useState([])
    const [selectedAgreement, setSelectedAgreement] = useState(null)
    const [carSearchInput, setCarSearchInput] = useState("")
    const [carSearchLoading, setCarSearchLoading] = useState(false)
    const [carSuggestions, setCarSuggestions] = useState([])
    const [showCarDropdown, setShowCarDropdown] = useState(false)
    const client = useApolloClient()

    const { data: reportedByData, loading: reportedByLoading } = useQuery(REPORTED_BY_ENUM_QUERY)
    const { data: incidentDocumentTypesData, loading: typesLoading } = useQuery(ALL_INCIDENT_DOCUMENT_TYPES)
    const { data: allEnumsData } = useQuery(ALL_ENUMS)
    const { data: branchTimezoneData } = useQuery(GetConfigurationSettings, {
        skip: !branchId,
        variables: { branchId, name: "PREFERRED_TIMEZONE" },
        fetchPolicy: "cache-first",
    })
    const branchTimezone = branchTimezoneData?.getConfigurationSettings || null
    const [createNewIncident, { loading: mutationLoading }] = useMutation(CREATE_NEW_INCIDENT_MUTATION)

    const { control, reset, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(IncidentSchema),
        defaultValues: {
            reportedBy: "",
            notificationDatetime: new Date(),
            incidentDate: "",
            incidentTime: "",
            description: "",
            carConditionId: "",
            incidentState: ""
        },
        mode: "onSubmit",
    })

    const ridesharePeriod = watch("ridesharePeriod")
    const incidentDate = watch("incidentDate")
    const incidentTime = watch("incidentTime")
    const incidentLocation = watch("location")
    const incidentState = watch("incidentState")
    const [subStep, setSubStep] = useState(1)
    const totalSubSteps = incidentDate ? 6 : 5
    useEffect(() => {
        if (subStep > totalSubSteps) setSubStep(totalSubSteps)
    }, [totalSubSteps, subStep])

    useEffect(() => {
        if (!incidentDate && subStep === 2) {
            setSubStep(3)
        }
    }, [incidentDate, subStep])

    const subNext = () => {
        setSubStep((s) => {
            const next = Math.min(s + 1, totalSubSteps)
            return !incidentDate && next === 2 ? 3 : next
        })
    }

    const subBack = () => {
        setSubStep((s) => {
            const prev = Math.max(s - 1, 1)
            return !incidentDate && prev === 2 ? 1 : prev
        })
    }
    const subProgress = Math.round((subStep / totalSubSteps) * 100)


    const steps = useMemo(() => ([
        { id: 1, title: "Details" },
        { id: 2, title: "Additional Info" },
        // { id: 3, title: "Parties" },
        { id: 3, title: "Documents" },
    ]), [])

    const debouncedCarSearch = useRef(
        debounce((searchTerm) => {
            if (!searchTerm) {
                setCarSuggestions([])
                setCarSearchLoading(false)
                return
            }
            setCarSearchLoading(true)
            client.query({
                query: ALL_CARS_WITH_AGREEMENTS_QUERY,
                variables: { searchTerm },
                fetchPolicy: 'network-only',
            }).then(result => {
                setCarSearchLoading(false)
                if (result.data && result.data.allCars && result.data.allCars.edges) {
                    setCarSuggestions(result.data.allCars.edges)
                } else {
                    setCarSuggestions([])
                }
            }).catch(() => {
                setCarSearchLoading(false)
                setCarSuggestions([])
            })
        }, 400)
    ).current

    const handleCarSearchInput = (e) => {
        const value = e.target.value
        setCarSearchInput(value)
        setShowCarDropdown(true)
        debouncedCarSearch(value)
    }

    const handleCarSelect = (carId) => {
        const carObj = carSuggestions.find(c => c.node.id === carId)?.node
        setSelectedCar(carId)
        setSelectedCarObj(carObj || null)
        setCarSearchInput("")
        setShowCarDropdown(false)
        setCarSuggestions([])
    }

    useEffect(() => {
        if (!incidentDate || !selectedCar) {
            setAgreements([])
            setSelectedAgreement(null)
            return
        }

        client.query({
            query: ALL_RENTALS_QUERY,
            variables: { carId: selectedCar, activeOn: incidentDate },
            fetchPolicy: 'network-only',
        }).then(result => {
            if (result?.data?.allRentals?.edges) {
                setAgreements(result.data.allRentals.edges.map(e => e.node))
                if (result.data.allRentals.edges.length === 1)
                    setSelectedAgreement(result.data.allRentals.edges[0].node)
                else
                    setSelectedAgreement(null)
            } else {
                setAgreements([])
                setSelectedAgreement(null)
            }
        }).catch(() => {
            setAgreements([])
            setSelectedAgreement(null)
        })
    }, [selectedCar, incidentDate])

    const handleClearCar = () => {
        setSelectedCar(null)
        setSelectedCarObj(null)
        setAgreements([])
        setSelectedAgreement(null)
        setCarSearchInput("")
        setShowCarDropdown(false)
    }

    const next = (data) => {
        if (step === 1) {
            let isCarSelected = agreementId || carConditionId || selectedCar ? true : false
            if (!isCarSelected || !data.reportedBy?.trim()) {
                setError("Please select a car and fill all required fields.")
                return
            }
        }

        if (step === 2 && !incidentDate) {
            setStep(3)
            return
        }

        setError(null)
        setStep((s) => Math.min(4, s + 1))
    }

    const back = () => {
        setError(null)
        setStep((s) => Math.max(1, s - 1))
    }

    // const handleAddVehicleParty = (partyPayload) => {
    //     setParties((prev) => [...prev, partyPayload]);
    // };

    // const handleAddPeopleParty = (peoplePayload) => {
    //     setParties((prev) => [...prev, peoplePayload]);
    // };

    // const removeParty = (idx) => {
    //     setParties((prev) => prev.filter((_, i) => i !== idx));
    // };

    const closeAndReset = () => {
        reset()
        // setParties([])
        setDocuments([])
        setSelectedDocType(null)
        setDocError(null)
        setStep(1)
        setSubStep(1)
        setIsLoading(false)
        setError(null)
        toggle()
    }

    const onDocTypeChange = (opt) => {
        setSelectedDocType(opt || null)
        setDocError(null)
    }

    const onFileSelected = (e) => {
        const file = e.target.files?.[0]
        if (!file) return

        if (!selectedDocType) {
            setDocError("Please select a document type first.")
            e.target.value = null
            return
        }

        const reader = new FileReader()
        reader.onload = () => {
            const base64String = reader.result.split(',')[1]
            setDocuments(prev => [...prev, {
                type: selectedDocType.value,
                file,
                base64: base64String,
                notes: ""
            }])
            setShowAddDocument(false)
        }
        reader.readAsDataURL(file)

        setSelectedDocType(null)
        e.target.value = null
    }

    const removeDocument = (idx) => {
        setDocuments(prev => prev.filter((_, i) => i !== idx))
    }

    const updateDocumentNotes = (idx, notes) => {
        setDocuments(prev => prev.map((doc, i) =>
            i === idx ? { ...doc, notes } : doc
        ))
    }

    const onSubmit = async (data) => {
        setIsLoading(true)
        try {
            const incidentData = {
                reportedBy: data.reportedBy,
                notificationDatetime: data.notificationDatetime || null,
                incidentDate: data.incidentDate || null,
                incidentTime: data.incidentTime || null,
                description: data.description || null,
                carId: selectedCar || null,

                incidentType: data.incidentType || null,
                // responsibleParty: data.responsibleParty || null,
                source: "BOS",

                ridesharePeriod: data.ridesharePeriod || null,
                ridesharePlatform: data.ridesharePlatform || null,
                ridesharePeriodSource: data.ridesharePeriodSource || null,

                carTotaled: data.carTotaled || null,
                // carOnHold: data.carOnHold,

                location: data.location || null,
                incidentState: data.incidentState || null,

                hasDashcamVideos: data.hasDashcamVideos || null,
                hasPoliceReport: data.hasPoliceReport || null,
                hasOtherParties: data.hasOtherParties || null,
                hasLiabilityClaim: data.hasLiabilityClaim || null,
                hasSubroClaim: data.hasSubroClaim || null,
            }

            if (carConditionId) {
                incidentData["carConditionId"] = carConditionId
            }

            if (agreementId) {
                incidentData["agreementId"] = agreementId
            } else if (selectedAgreement) {
                incidentData["agreementId"] = selectedAgreement.id
            }

            // const partiesData = parties.length > 0 ? parties.map(party => {
            //     const partyData = {
            //         partyName: party.partyName || party.name,
            //         partyType: party.partyType || party.role,
            //     };
            //     if (party.partyType === "VEHICLE" && party.partyVehicle) {
            //         partyData.partyVehicle = party.partyVehicle;
            //     }
            //     if (party.partyPeople && Array.isArray(party.partyPeople)) {
            //         partyData.partyPeople = party.partyPeople;
            //     } else if (party.partyType !== "VEHICLE") {
            //         partyData.partyPeople = [{
            //             name: party.name,
            //             phoneNumber: party.phoneNumber,
            //             email: party.email,
            //             address: party.address,
            //             driverLicense: party.driverLicense,
            //             injuries: party.injuries,
            //             statement: party.statement,
            //             role: party.role
            //         }];
            //     }
            //     return partyData;
            // }) : [];

            const documentsData = documents.length > 0 ? documents.map(doc => ({
                documentType: doc.type,
                document: doc.base64,
                notes: doc.notes || "",
            })) : []
            const result = await createNewIncident({
                variables: {
                    input: {
                        incident: incidentData,
                        // parties: partiesData,
                        documents: documentsData
                    }
                }
            })

            if (result.data.createNewIncident.ok) {
                if (refetchQuery) refetchQuery()
                closeAndReset()
            } else {
                const errors = result.data.createNewIncident.errors
                setError(errors[0].messages[0])
            }
        } catch (error) {
            setError(error.message)
            setIsLoading(false)
        } finally {
            setIsLoading(false)
        }
    }

    const reportedByOptions = reportedByData?.reportedByChoices?.enumValues?.map(opt => ({
        value: opt.name, label: opt.description || opt.name
    })) || []

    const incidentDocumentTypesOptions = incidentDocumentTypesData?.allIncidentDocumentTypes?.edges?.map(opt => ({
        value: opt.node.id, label: opt.node.name
    })) || []

    const mapEnumToOptions = (enumValues) =>
        enumValues?.map(({ name, description }) => ({ value: name, label: description || name }))

    const ridesharePlatformOptions = mapEnumToOptions(allEnumsData?.ridesharePlatformChoices?.enumValues)
    const ridesharePeriodOptions = mapEnumToOptions(allEnumsData?.ridesharePeriodChoices?.enumValues)
    const ridesharePeriodSourceOptions = mapEnumToOptions(allEnumsData?.ridesharePeriodSourceChoices?.enumValues)
    // const responsiblePartyOptions = mapEnumToOptions(allEnumsData?.responsiblePartyChoices?.enumValues);
    // const sourceOptions = mapEnumToOptions(allEnumsData?.sourceChoices?.enumValues);
    const incidentTypeOptions = mapEnumToOptions(allEnumsData?.incidentTypeChoices?.enumValues)
    const carTotaledOptions = mapEnumToOptions(allEnumsData?.carTotaledChoices?.enumValues)
    const yesNoOptions = [{ value: "YES", label: "Yes" }, { value: "NO", label: "No" }]
    const booleanOptions = [{ value: true, label: "Yes" }, { value: false, label: "No" }]
    const stateOptions = useMemo(() => getStatesByCountry("ALL"), [])
    const isIncidentStateMissing = Boolean(incidentLocation && !incidentState)

    return (
        <>
            <Modal className="add-new-incident-modal" isOpen={isOpen} size="lg">
                <ModalHeader className="text-capitalize">
                    Add New Incident<br />
                    <small className="text-muted">Submit a new claim by providing incident details.</small>
                </ModalHeader>
                <ModalBody>
                    {error && (<div className="alert alert-danger" role="alert">{error}</div>)}

                    <div className="d-flex align-items-center justify-content-between mb-2">
                        {steps.map((s, i) => (
                            <div key={s.id} className="d-flex align-items-center">
                                <p className="step-circle" style={{ background: step >= s.id ? "#0B80F9" : "#e9ecef", color: step >= s.id ? "#fff" : "#6c757d" }}>
                                    {s.id}
                                </p>
                                <p className="step-title" style={{ fontWeight: step === s.id ? 600 : 500, color: step >= s.id ? "#1f2937" : "#6c757d" }}>{s.title}</p>
                                {i < steps.length - 1 && (<p className="step-divider" style={{ background: step > s.id ? "#0B80F9" : "#e9ecef" }} />)}
                            </div>
                        ))}
                    </div>

                    <Form onSubmit={handleSubmit(step < 4 ? next : onSubmit)}>
                        {step === 1 && (
                            <>
                                <div className="border rounded p-3 mb-2">
                                    {/* <Row className="mb-4">
                                        <Col>
                                            <h5 className="text-capitalize">Car Details</h5>
                                            <small className="text-muted text-capitalize">Please provide the car details.</small>
                                        </Col>
                                    </Row> */}
                                    {(!agreementId && !carConditionId && !carId) && <Row>
                                        <Col>
                                            <FormGroup>
                                                <Label>Car *</Label>
                                                <div>
                                                    {selectedCar ? (
                                                        <div className="d-flex align-items-center justify-content-between border rounded p-2" style={{ minHeight: 48 }}>
                                                            {selectedCarObj && (
                                                                <span className="me-2">
                                                                    {selectedCarObj.year} {selectedCarObj.carModel?.make} {selectedCarObj.carModel?.name} {selectedCarObj.carModel?.series && `(${selectedCarObj.carModel.series})`}
                                                                    <br /> <small className="text-muted">{selectedCarObj.pk} - {selectedCarObj.vin}</small>
                                                                </span>
                                                            )}
                                                            <Button onClick={handleClearCar} style={{ background: "transparent", border: "none", color: "red" }}>
                                                                <i className="fa fa-times" />
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <Input
                                                                type="text"
                                                                value={carSearchInput}
                                                                onChange={handleCarSearchInput}
                                                                onFocus={() => setShowCarDropdown(true)}
                                                                placeholder="Search by Car ID, VIN"
                                                                autoComplete="off"
                                                            />
                                                            {showCarDropdown && (
                                                                <div className="option-list car-search-dropdown">
                                                                    {carSearchLoading ? (
                                                                        <p className="p-2 mb-0 text-center text-muted">Loading cars, please wait...</p>
                                                                    ) : carSuggestions.length > 0 ? (
                                                                        carSuggestions.map((car, idx) => (
                                                                            <Row key={idx} className="option py-1" style={{ cursor: 'pointer' }} onClick={() => handleCarSelect(car.node.id)}>
                                                                                <Col>
                                                                                    <p className="mb-0" style={{ fontWeight: 500 }}>
                                                                                        {car.node.year} {car.node.carModel?.make} {car.node.carModel?.name} {car.node.carModel?.series && `(${car.node.carModel.series})`}<br />
                                                                                        <small>{car.node.pk || ""} - {car.node.vin || ""}</small>
                                                                                    </p>
                                                                                </Col>
                                                                            </Row>
                                                                        ))
                                                                    ) : (
                                                                        <p className="p-2 mb-0 text-center text-muted">No cars found.</p>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>}
                                    <Row>
                                        <Col md="6">
                                            <FormGroup className="mb-3">
                                                <Label htmlFor="incidentDate">Date of Incident</Label>
                                                <Controller
                                                    name="incidentDate"
                                                    control={control}
                                                    render={({ field }) => (<Input id="incidentDate" type="date" {...field} max={maxIncidentDate ? moment(maxIncidentDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')} />)}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup className="mb-3">
                                                <Label htmlFor="incidentTime">Time of Incident</Label>
                                                <Controller
                                                    name="incidentTime"
                                                    control={control}
                                                    render={({ field }) => (<Input id="incidentTime" type="time" {...field} />)}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    {selectedCar && incidentDate && (!agreementId && !carConditionId) && <Row>
                                        <Col>
                                            <FormGroup>
                                                <Label>Agreement</Label>
                                                <Select
                                                    options={agreements.map(a => ({ value: a.id, agreement: a }))}
                                                    value={agreements.find(a => a.id === selectedAgreement?.id) ? { value: selectedAgreement.id, agreement: selectedAgreement } : null}
                                                    onChange={opt => setSelectedAgreement(opt ? opt.agreement : null)}
                                                    placeholder={selectedCar ? incidentDate ? 'Select Agreement' : 'Add Incident Date/Time' : 'Select a car first'}
                                                    isDisabled={!selectedCar || !incidentDate || agreements.length === 1}
                                                    isClearable={agreements?.length > 1}
                                                    formatOptionLabel={(opt) => {
                                                        const agreement = opt.agreement
                                                        if (!agreement) return null
                                                        return (
                                                            <p className="mb-0" style={{ fontWeight: 500 }}>
                                                                {(agreement.driver?.name || 'No Driver') + ' - ' + (agreement.agreementType || '')}<br />
                                                                <small>{moment(agreement.startDate).format('MMM D, YYYY')} — {agreement.endDate ? moment(agreement.endDate).format('MMM D, YYYY') : 'ACTIVE'}</small>
                                                            </p>
                                                        )
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>}
                                </div>

                                <div className="border rounded p-3">
                                    {/* <Row className="mb-4">
                                        <Col>
                                            <h5 className="text-capitalize">Incident Details</h5>
                                            <small className="text-muted text-capitalize">When and where did the incident occur?</small>
                                        </Col>
                                    </Row> */}
                                    <Row>
                                        <Col md="6">
                                            <FormGroup className="mb-3">
                                                <Label htmlFor="reportedBy">Reported By *</Label>
                                                <Controller
                                                    name="reportedBy"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            className="bos-custom-select"
                                                            classNamePrefix="bos-select"
                                                            inputId="reportedBy"
                                                            options={reportedByOptions}
                                                            placeholder="Select Reporter"
                                                            isClearable
                                                            isLoading={reportedByLoading}
                                                            value={reportedByOptions.find(opt => opt.value === field.value) || null}
                                                            onChange={(opt) => field.onChange(opt ? opt.value : "")}
                                                            onBlur={field.onBlur}
                                                        />
                                                    )}
                                                />
                                                {errors.reportedBy && <p className="invalid-feedback d-block">{errors.reportedBy.message}</p>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup className="mb-3">
                                                <Label htmlFor="notificationDatetime">Notification Date/Time</Label>
                                                <Controller
                                                    name="notificationDatetime"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <PreferredDateTimeContext.Consumer>
                                                            {({ preferredDateTimeInfo }) => {
                                                                const tz = branchTimezone || preferredDateTimeInfo.timezone
                                                                return (
                                                                    <DatetimePickerWithTimeZone
                                                                        key={tz}
                                                                        type="datetime-local"
                                                                        name={field.name}
                                                                        id="notificationDatetime"
                                                                        timezone={tz}
                                                                        value={field.value ?? ""}
                                                                        max={moment().tz(tz).format(moment.HTML5_FMT.DATETIME_LOCAL)}
                                                                        placeholder="Select notification datetime"
                                                                        updateInput={(_name, value) => { field.onChange(value) }}
                                                                        setError={() => { setError() }}
                                                                    />
                                                                )
                                                            }}
                                                        </PreferredDateTimeContext.Consumer>
                                                    )}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <FormGroup className="mb-3">
                                        <Label htmlFor="description">Detailed Description</Label>
                                        <Controller
                                            name="description"
                                            control={control}
                                            render={({ field }) => (<Input id="description" type="textarea" placeholder="Describe what happened..." {...field} />)}
                                        />
                                    </FormGroup>
                                </div>
                            </>
                        )}

                        {step === 2 && (
                            <div className="border rounded p-3">
                                <Row className="mb-4">
                                    <Col>
                                        <h6 className="text-uppercase mb-1">Additional Information</h6>
                                        <Progress className="mt-2" style={{ height: 6 }} value={subProgress} />
                                    </Col>
                                </Row>

                                {/* SUB-STEP CONTENT */}
                                <div style={{ minHeight: "250px" }}>
                                    {subStep === 1 && (
                                        <Row>
                                            <Col xs={12}>
                                                <FormGroup className="mb-3">
                                                    <small htmlFor="incidentType">Incident Type</small>
                                                    <Controller
                                                        name="incidentType"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="incidentType"
                                                                options={incidentTypeOptions}
                                                                placeholder="Select Incident Type"
                                                                isClearable
                                                                value={incidentTypeOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            {/* <Col xs={12}>
                                                <FormGroup className="mb-3">
                                                    <small htmlFor="responsibleParty">Responsible Party</small>
                                                    <Controller
                                                        name="responsibleParty"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="responsibleParty"
                                                                options={responsiblePartyOptions}
                                                                placeholder="Select Responsible Party"
                                                                isClearable
                                                                value={responsiblePartyOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col> */}
                                        </Row>
                                    )}

                                    {subStep === 2 && incidentDate && (
                                        <Row>
                                            <Col xs={12}>
                                                <h5 className="text-capitalize">ARGYLE GIGS on {incidentDate} {incidentTime ? `at ${incidentTime}` : ""}</h5>
                                                {incidentDate && selectedAgreement?.driver && <RenderArgyleGigs driverId={selectedAgreement?.driver?.id} incidentDate={incidentDate} incidentTime={incidentTime} />}
                                            </Col>
                                            <Col xs={12}>
                                                <FormGroup className="mb-3">
                                                    <small htmlFor="ridesharePeriod">Rideshare Period</small>
                                                    <Controller
                                                        name="ridesharePeriod"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="ridesharePeriod"
                                                                options={ridesharePeriodOptions}
                                                                placeholder=""
                                                                isClearable
                                                                value={ridesharePeriodOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>

                                            {ridesharePeriod && !["X", "ZERO"].includes(ridesharePeriod) && (
                                                <>
                                                    <Col xs={12}>
                                                        <FormGroup className="mb-3">
                                                            <small htmlFor="ridesharePlatform">Rideshare Platform *</small>
                                                            <Controller
                                                                name="ridesharePlatform"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <Select
                                                                        className="bos-custom-select"
                                                                        classNamePrefix="bos-select"
                                                                        inputId="ridesharePlatform"
                                                                        options={ridesharePlatformOptions}
                                                                        placeholder="Select Rideshare Platform"
                                                                        isClearable
                                                                        value={ridesharePlatformOptions?.find(opt => opt.value === field.value) || null}
                                                                        onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                                    />
                                                                )}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col xs={12}>
                                                        <FormGroup className="mb-0">
                                                            <small htmlFor="ridesharePeriodSource">Rideshare Period Source *</small>
                                                            <Controller
                                                                name="ridesharePeriodSource"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <Select
                                                                        className="bos-custom-select"
                                                                        classNamePrefix="bos-select"
                                                                        inputId="ridesharePeriodSource"
                                                                        options={ridesharePeriodSourceOptions}
                                                                        placeholder="Select Rideshare Period Source"
                                                                        isClearable
                                                                        value={ridesharePeriodSourceOptions?.find(opt => opt.value === field.value) || null}
                                                                        onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                                    />
                                                                )}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </>
                                            )}
                                        </Row>
                                    )}

                                    {subStep === 3 && (
                                        <Row>
                                            <Col xs={12}>
                                                <FormGroup className="mb-3">
                                                    <small htmlFor="carTotaled">Car Totaled</small>
                                                    <Controller
                                                        name="carTotaled"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="carTotaled"
                                                                options={carTotaledOptions}
                                                                placeholder="Select if car was totaled"
                                                                isClearable
                                                                value={carTotaledOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            {/* <Col xs={12}>
                                                <FormGroup className="mb-0">
                                                    <small htmlFor="carOnHold">Car On Hold</small>
                                                    <Controller
                                                        name="carOnHold"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="carOnHold"
                                                                options={booleanOptions}
                                                                placeholder="Select an option"
                                                                isClearable
                                                                value={booleanOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col> */}
                                        </Row>
                                    )}

                                    {subStep === 4 && (
                                        <Row>
                                            <Col xs={12}>
                                                <MapPicker
                                                    onLocationSelect={(loc) => {
                                                        setValue("location", loc.address)
                                                        setValue("incidentState", loc.state || "")
                                                    }}
                                                />
                                                {incidentLocation &&
                                                    <div className="mt-2">
                                                        <p className="mb-0 small">
                                                            <strong>Address:</strong> {incidentLocation}&nbsp;&nbsp;
                                                        </p>
                                                        <FormGroup className="mt-2 mb-0">
                                                            <div className="d-flex align-items-center">
                                                                <small htmlFor="incidentState" className="me-2"><strong>State:&nbsp;&nbsp;</strong></small>
                                                                <div style={{ width: 230 }}>
                                                                    <Controller
                                                                        name="incidentState"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <Select
                                                                                className="bos-custom-select"
                                                                                classNamePrefix="bos-select"
                                                                                inputId="incidentState"
                                                                                options={stateOptions}
                                                                                placeholder="Select state"
                                                                                isClearable
                                                                                value={stateOptions.find(opt => opt.value === field.value) || (field.value ? { value: field.value, label: field.value } : null)}
                                                                                onChange={(opt) => field.onChange(opt ? opt.value : "")}
                                                                            />
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {isIncidentStateMissing && <p className="invalid-feedback d-block">Please select the incident state.</p>}
                                                        </FormGroup>
                                                    </div>
                                                }
                                            </Col>
                                        </Row>
                                    )}

                                    {subStep === 5 && (
                                        <Row>
                                            <Col xs={12}>
                                                <FormGroup className="mb-3">
                                                    <small htmlFor="hasDashcamVideos">Has Dashcam Video?</small>
                                                    <Controller
                                                        name="hasDashcamVideos"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="hasDashcamVideos"
                                                                options={yesNoOptions}
                                                                placeholder=""
                                                                isClearable
                                                                value={yesNoOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12}>
                                                <FormGroup className="mb-3">
                                                    <small htmlFor="hasPoliceReport">Has Police Report?</small>
                                                    <Controller
                                                        name="hasPoliceReport"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="hasPoliceReport"
                                                                options={yesNoOptions}
                                                                placeholder=""
                                                                isClearable
                                                                value={yesNoOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12}>
                                                <FormGroup className="mb-0">
                                                    <small htmlFor="hasOtherParties">Has Other Parties?</small>
                                                    <Controller
                                                        name="hasOtherParties"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="hasOtherParties"
                                                                options={yesNoOptions}
                                                                placeholder=""
                                                                isClearable
                                                                value={yesNoOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    )}

                                    {subStep === 6 && incidentDate && (
                                        <Row>
                                            <Col xs={12}>
                                                <FormGroup className="mb-0">
                                                    <small htmlFor="hasLiabilityClaim">Has Liability Claim?</small>
                                                    <Controller
                                                        name="hasLiabilityClaim"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="hasLiabilityClaim"
                                                                options={yesNoOptions}
                                                                placeholder="Select an option"
                                                                isClearable
                                                                value={yesNoOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12}>
                                                <FormGroup className="mb-0">
                                                    <small htmlFor="hasSubroClaim">Has Subrogation Claim?</small>
                                                    <Controller
                                                        name="hasSubroClaim"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                className="bos-custom-select"
                                                                classNamePrefix="bos-select"
                                                                inputId="hasSubroClaim"
                                                                options={yesNoOptions}
                                                                placeholder="Select an option"
                                                                isClearable
                                                                value={yesNoOptions?.find(opt => opt.value === field.value) || null}
                                                                onChange={(opt) => field.onChange(opt ? opt.value : null)}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    )}
                                </div>

                                {/* Sub-step navigation */}
                                <div className="d-flex justify-content-between mt-3">
                                    <Button color="link" onClick={subBack} disabled={subStep === 1}>Back</Button>
                                    <Button color="link" onClick={subNext} disabled={subStep === totalSubSteps}>Next</Button>
                                </div>
                            </div>
                        )}

                        {/* {step === 3 && (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        <h6>Involved Parties</h6>
                                        <p className="text-muted small">Add parties involved in this incident.</p>
                                    </div>
                                    <UncontrolledDropdown direction="down">
                                        <DropdownToggle color="outline-primary" size="sm" caret>
                                            + Add Party
                                        </DropdownToggle>

                                        <DropdownMenu end>
                                            <DropdownItem onClick={() => setVehicleModalOpen(true)}>
                                                <i className="fa fa-car" /> Vehicle Party
                                            </DropdownItem>
                                            <DropdownItem onClick={() => { setPeoplePartyType("NON_MOTORIST"); setPeopleModalOpen(true); }}>
                                                <i className="fa fa-street-view" /> Non Motorist Party
                                            </DropdownItem>
                                            <DropdownItem onClick={() => { setPeoplePartyType("WITNESS"); setPeopleModalOpen(true); }}>
                                                <i className="fa fa-user" /> Witness Party
                                            </DropdownItem>
                                            <DropdownItem onClick={() => { setPeoplePartyType("UNAUTHORIZED_DRIVER"); setPeopleModalOpen(true); }}>
                                                <i className="fa fa-user-times" /> Unauthorized Driver
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                </div>
                                <div className="border rounded overflow-y">
                                    {parties.length === 0 ? (
                                        <p className="text-danger small p-3">No parties added.</p>
                                    ) : (
                                        <>
                                            {parties.flatMap((p, idx) => {
                                                return (
                                                    <div key={`veh-${idx}`} className="d-flex align-items-start justify-content-between px-2 pt-2 border rounded m-2">
                                                        {p.partyType === "VEHICLE" ?
                                                            <div>
                                                                <h6 className="fw-semibold"> Vehicle {p.partyName ? `(${p.partyName})` : ""}</h6>
                                                                <p className="small text-muted">
                                                                    <strong>{p.partyVehicle?.year || "--"}  {p.partyVehicle?.make || "--"} {p.partyVehicle?.model || "--"}<br /></strong>
                                                                    VIN: {p.partyVehicle?.vin || "--"}<br />
                                                                    Plate: {p.partyVehicle?.plateNumber || "--"} <br />
                                                                    Insurance Company: {p.partyVehicle?.insuranceCompany || "--"} <br />
                                                                    Policy #: {p.partyVehicle?.policyNumber || "--"} <br />
                                                                    Damage Description: {p.partyVehicle?.damage || "--"} <br />
                                                                </p>
                                                                {Array.isArray(p.partyPeople) && p.partyPeople.length > 0 && (
                                                                    <div className="mt-2">
                                                                        {p.partyPeople.map((pp, i) => (
                                                                            <React.Fragment key={`vehp-${idx}-${i}`}>
                                                                                <h6>{pp.role ? toTitleCase(pp.role) : "--"}</h6>
                                                                                <p className="small text-muted">
                                                                                    Name: {pp.name || "--"}<br />
                                                                                    Phone: {pp.phoneNumber || "--"}<br />
                                                                                    Email: {pp.email || "--"}<br />
                                                                                    License: {pp.driverLicense || "--"}<br />
                                                                                    Injuries: {pp.injuries || "--"}<br />
                                                                                    Address: {pp.address || "--"}<br />
                                                                                    Statement: {pp.statement || "--"}
                                                                                </p>
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            :
                                                            <div>
                                                                <h6>{toTitleCase(p.partyType || p.role)}</h6>
                                                                <p className="small text-muted">
                                                                    <strong> Role: {p.role ? toTitleCase(p.role) : "--"}</strong><br />
                                                                    Name: {p.name || "-"}<br />
                                                                    Phone: {p.phoneNumber || "--"}<br />
                                                                    Email: {p.email || "--"}<br />
                                                                    License: {p.driverLicense || "--"}<br />
                                                                    Injuries: {p.injuries || "--"}<br />
                                                                    Address: {p.address || "--"}<br />
                                                                    Statement: {p.statement || "--"}
                                                                </p>
                                                            </div>
                                                        }
                                                        <i className="fa fa-trash fa-lg text-danger " aria-hidden="true" onClick={() => removeParty(idx)} />
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            </>
                        )} */}

                        {step === 3 && (
                            <div className="border rounded p-3">
                                <Row>
                                    <Col>
                                        {showAddDocument && (
                                            <>
                                                <FormGroup>
                                                    <Label htmlFor="documentType">Document Type</Label>
                                                    <Select
                                                        inputId="documentType"
                                                        className="bos-custom-select"
                                                        classNamePrefix="bos-select"
                                                        placeholder="Select document type"
                                                        options={incidentDocumentTypesOptions}
                                                        isClearable
                                                        isLoading={typesLoading}
                                                        value={selectedDocType}
                                                        onChange={onDocTypeChange}
                                                    />
                                                    {docError && <p className="text-danger small pt-1">{docError}</p>}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label htmlFor="incident_document">Upload Document</Label>
                                                    <Input
                                                        id="incident_document"
                                                        type="file"
                                                        onChange={onFileSelected}
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        disabled={!selectedDocType}
                                                        style={{ border: "none" }}
                                                    />
                                                </FormGroup>
                                            </>
                                        )}
                                        {documents.length > 0 && documents.map((d, i) => (
                                            <div key={`${d.type}-${i}`} className="border rounded p-3 mb-3">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <p className="mb-1 fw-semibold">
                                                        {toTitleCase(incidentDocumentTypesOptions.find(o => o.value === d.type)?.label || d.type)}&nbsp;
                                                        <span className="text-muted small ms-2">
                                                            ({(d.file.size / 1024 < 1024 ? `${Math.round(d.file.size / 1024)} KB` : `${(d.file.size / 1024 / 1024).toFixed(2)} MB`)})
                                                        </span>
                                                    </p>
                                                    <i className="fa fa-trash fa-lg text-danger" aria-hidden="true" onClick={() => {
                                                        removeDocument(i)
                                                        if (documents.length === 1) setShowAddDocument(true)
                                                    }} style={{ cursor: 'pointer' }} />
                                                </div>
                                                <FormGroup className="mb-0">
                                                    <Input
                                                        id={`doc-notes-${i}`}
                                                        type="textarea"
                                                        placeholder="Add notes to this document..."
                                                        value={d.notes || ""}
                                                        onChange={(e) => updateDocumentNotes(i, e.target.value)}
                                                    />
                                                </FormGroup>
                                            </div>
                                        ))}
                                        {documents.length > 0 && !showAddDocument && (
                                            <Button className="float-right" color="primary" outline onClick={() => setShowAddDocument(true)}>
                                                <i className="fa fa-plus" aria-hidden="true" /> Add More
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        )}

                        <button type="submit" className="d-none" />
                    </Form>
                </ModalBody>

                <ModalFooter className="d-flex justify-content-between">
                    <Button disabled={isLoading || mutationLoading} color="secondary" onClick={closeAndReset}>Cancel</Button>
                    <div>
                        {step > 1 && (
                            <Button disabled={isLoading || mutationLoading} color="link" onClick={back}>&lt; Back</Button>
                        )}
                        {step < 3 ? (
                            <Button
                                disabled={
                                    isLoading ||
                                    mutationLoading ||
                                    (step === 2 && subStep < totalSubSteps) // main Next disabled until last sub-step
                                }
                                color="primary"
                                onClick={handleSubmit(next)}
                            >
                                Next &gt;
                            </Button>
                        ) : (
                            <Button color="primary" onClick={handleSubmit(onSubmit)} disabled={isLoading || mutationLoading}>
                                {isLoading || mutationLoading ? "Submitting..." : "Submit"}
                            </Button>
                        )}
                    </div>
                </ModalFooter>
            </Modal>

            {/* {vehicleModalOpen && (
                <VehiclePartyModal
                    isOpen={vehicleModalOpen}
                    toggle={() => setVehicleModalOpen(false)}
                    onCreate={(party) => {
                        handleAddVehicleParty(party);
                        setVehicleModalOpen(false);
                    }}
                />
            )}
            {peopleModalOpen && (
                <AddPeopleModal
                    isOpen={peopleModalOpen}
                    toggle={() => setPeopleModalOpen(false)}
                    partyType={peoplePartyType}
                    refetchQuery={null}
                    onCreate={(party) => {
                        handleAddPeopleParty(party);
                        setPeopleModalOpen(false);
                    }}
                />
            )} */}
        </>
    )
}
